import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';

interface ConversionHeroProps {
  headline: string;
  subheadline: string;
  primaryCTA: {
    text: string;
    onClick: () => void;
    variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  };
  secondaryCTA?: {
    text: string;
    onClick: () => void;
    variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
    badge?: string;
  };
  trustBadges?: string[];
  backgroundImage?: string;
  rightContent?: React.ReactNode;
  className?: string;
}

const ConversionHero: React.FC<ConversionHeroProps> = ({
  headline,
  subheadline,
  primaryCTA,
  secondaryCTA,
  trustBadges = [],
  backgroundImage,
  rightContent,
  className = ''
}) => {
  return (
    <section 
      className={`relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-accent/5 ${className}`}
      style={backgroundImage ? { backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
    >
      <div className="container mx-auto px-4 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-foreground">
              {headline}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              {subheadline}
            </p>
            
            {/* Trust Badges */}
            {trustBadges.length > 0 && (
              <div className="flex flex-wrap gap-4 pt-4">
                {trustBadges.map((badge, index) => (
                  <Badge key={index} variant="secondary" className="text-sm py-2 px-4">
                    <Check className="w-4 h-4 mr-2 text-primary" />
                    {badge}
                  </Badge>
                ))}
              </div>
            )}
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                size="lg"
                onClick={primaryCTA.onClick}
                variant={primaryCTA.variant || 'default'}
                className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
              >
                {primaryCTA.text}
              </Button>
              
              {secondaryCTA && (
                <Button
                  size="lg"
                  onClick={secondaryCTA.onClick}
                  variant={secondaryCTA.variant || 'outline'}
                  className="text-lg px-8 py-6 relative"
                >
                  {secondaryCTA.badge && (
                    <span className="absolute -top-2 -right-2 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                    </span>
                  )}
                  {secondaryCTA.text}
                </Button>
              )}
            </div>
          </div>
          
          {/* Right Content */}
          {rightContent && (
            <div className="relative">
              {rightContent}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ConversionHero;
